import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  DollarSign,
  User,
  Calendar,
  FileText,
  Banknote,
  RefreshCw
} from 'lucide-react';
import { formatCurrency, formatDate, redemptionsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const RedemptionReview = () => {
  const navigate = useNavigate();
  const { getDisplayName } = useAuth();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [redemptionRequests, setRedemptionRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState(null);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Fetch redemption requests from database
  const fetchRedemptionRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Fetching redemption requests from API...');

      // Get all redemptions for issuer review (not just pending approval)
      const response = await redemptionsAPI.getAllRedemptions({
        // Get more redemptions to show full history
        limit: 50,
        // Add any issuer-specific filtering if needed
        // For now, get all redemptions that an issuer would need to review
      });

      console.log('📋 API Response:', response);
      console.log('📋 Full response structure:', JSON.stringify(response.data, null, 2));

      if (response.data.success) {
        // getAllRedemptions returns data in response.data.data, not response.data.data directly
        const redemptionsData = response.data.data || [];
        console.log('✅ API Success - Data:', redemptionsData);
        console.log('📊 Number of redemptions:', redemptionsData.length || 0);

        // Transform the API response to match the expected format
        const transformedRequests = redemptionsData.map(request => {
          console.log('🔄 Transforming request:', request);
          console.log('🏷️ Bond info in request:', {
            bondId: request.bondId,
            bondName: request.bondName,
            extractedBondName: request.bondId?.bondName || request.bondName || 'Unknown Bond'
          });

          return {
            id: request._id,
            requestId: request.redemptionId || request._id,
            bondName: request.bondId?.bondName ||
              request.bondName ||
              request.bond?.bondName ||
              request.bond?.name ||
              `Bond ${request.bondId?._id || request.bondId || 'Unknown'}`,
            bondId: request.bondId?._id || request.bondId,
            investorId: request.investorId,
            investorName: request.investorName || 'Unknown Investor',
            tokenAmount: request.tokenAmount || 0,
            redemptionValue: request.totalRedemptionAmount || 0,
            principalAmount: request.principalAmount || 0,
            accruedInterest: request.couponAmount || 0,
            requestDate: request.redemptionDate || request.createdAt,
            maturityDate: request.redemptionDate,
            status: mapApiStatusToDisplayStatus(request.status),
            urgency: determineUrgency(request),
            walletAddress: request.whitelistedWallet || 'Not provided',
            paymentMethod: 'Bank Transfer',
            bankDetails: {
              accountName: request.investorName || 'Unknown',
              accountNumber: 'Not provided',
              bankName: 'Not provided',
              swiftCode: 'Not provided'
            },
            isMatured: request.redemptionType === 'Maturity',
            reason: request.redemptionType === 'Early Redemption' ? 'Early redemption' : 'Maturity redemption',
            approvalDate: request.approvedAt,
            approvedBy: request.approvedBy?.firstName ? `${request.approvedBy.firstName} ${request.approvedBy.lastName}` : request.approvedBy,
            rejectionDate: request.rejectedAt,
            rejectedBy: request.rejectedBy?.firstName ? `${request.rejectedBy.firstName} ${request.rejectedBy.lastName}` : request.rejectedBy,
            rejectionReason: request.rejectionReason,
            transactionHash: request.transactionHash || null,
            notes: request.notes
          };
        });

        console.log('✅ Transformed requests:', transformedRequests);
        setRedemptionRequests(transformedRequests);
      } else {
        const errorMsg = response.data.message || 'Failed to fetch redemption requests';
        console.error('❌ API Error:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('❌ Error fetching redemption requests:', error);

      if (error.response?.status === 404) {
        // No redemptions found - this is not an error state
        console.log('ℹ️ No redemptions found (404), showing empty state');
        setRedemptionRequests([]);
      } else {
        setError(error.response?.data?.message || error.message || 'Failed to fetch redemption requests');
        setRedemptionRequests([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Map API status to display status
  const mapApiStatusToDisplayStatus = (apiStatus) => {
    const statusMap = {
      'Pending Approval': 'Pending Review', // Submitted and waiting for issuer approval
      'Approved': 'Approved', // Issuer has approved
      'Rejected': 'Rejected', // Issuer has rejected
      'Processing': 'Processing', // Currently being processed
      'Completed': 'Completed', // Successfully completed
      'Failed': 'Failed' // Processing failed
    };
    return statusMap[apiStatus] || apiStatus; // Return original if not mapped
  };

  // Determine urgency based on redemption data
  const determineUrgency = (request) => {
    if (request.redemptionType === 'maturity') {
      const redemptionDate = new Date(request.redemptionDate);
      const today = new Date();
      const daysUntilRedemption = Math.ceil((redemptionDate - today) / (1000 * 60 * 60 * 24));

      if (daysUntilRedemption <= 7) return 'High';
      if (daysUntilRedemption <= 30) return 'Medium';
      return 'Low';
    }
    return 'Medium'; // Early redemptions are medium priority by default
  };

  // Use effect to fetch data on component mount and refresh trigger
  useEffect(() => {
    fetchRedemptionRequests();
  }, [refreshTrigger]);

  // Filter requests based on status filter
  const filteredRequests = redemptionRequests.filter(request => {
    if (statusFilter === 'all') return true;
    return request.status.toLowerCase().replace(/\s+/g, '_') === statusFilter;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending Review': { color: 'warning', icon: Clock },
      'Approved': { color: 'success', icon: CheckCircle },
      'Rejected': { color: 'error', icon: XCircle }
    };
    const config = statusConfig[status] || { color: 'secondary', icon: Clock };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyConfig = {
      'High': { color: 'red', text: 'High Priority' },
      'Medium': { color: 'yellow', text: 'Medium' },
      'Low': { color: 'green', text: 'Low' }
    };
    const config = urgencyConfig[urgency] || { color: 'gray', text: urgency };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        {config.text}
      </span>
    );
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setShowApprovalModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setShowRejectionModal(true);
  };

  const confirmApproval = async () => {
    if (!selectedRequest) return;

    try {
      // Call API to approve redemptions - transaction hash will be generated by backend
      const response = await redemptionsAPI.approveRedemptions({
        redemptionIds: [selectedRequest.id],
        notes: `Approved by ${getDisplayName()} on ${new Date().toISOString()}`
      });

      if (response.data.success) {
        // Get the transaction hash from the approved redemption data
        const approvedRedemption = response.data.data?.[0];
        const transactionHash = approvedRedemption?.transactionHash || 'Generated by backend';

        setSuccessMessage(`✅ Redemption request approved successfully!
        
Bond ID: ${selectedRequest.bondId}
Investor: ${selectedRequest.investorName}
Bond: ${selectedRequest.bondName}
Amount: ${formatCurrency(selectedRequest.redemptionValue)}
Transaction Hash: ${transactionHash}

The redemption is now ready for processing and settlement.`);

        // Refresh data to show the updated transaction hash from database
        setRefreshTrigger(prev => prev + 1);
      } else {
        throw new Error(response.data.message || 'Failed to approve redemption');
      }
    } catch (error) {
      console.error('Error approving redemption request:', error);
      alert('Failed to approve redemption request: ' + error.message);
    }

    setShowApprovalModal(false);
    setSelectedRequest(null);
  };

  const confirmRejection = async () => {
    if (!selectedRequest) return;

    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }

    try {
      // Call API to reject redemptions
      const response = await redemptionsAPI.rejectRedemptions({
        redemptionIds: [selectedRequest.id],
        reason: rejectionReason
      });

      if (response.data.success) {
        setSuccessMessage(`❌ Redemption request rejected.
        
Bond ID: ${selectedRequest.bondId}
Investor: ${selectedRequest.investorName}
Reason: ${rejectionReason}

The rejection has been recorded and the request status updated.`);

        // Refresh data
        setRefreshTrigger(prev => prev + 1);
      } else {
        throw new Error(response.data.message || 'Failed to reject redemption');
      }
    } catch (error) {
      console.error('Error rejecting redemption request:', error);
      alert('Failed to reject redemption request: ' + error.message);
    }

    setShowRejectionModal(false);
    setSelectedRequest(null);
    setRejectionReason('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Redemption Review</h1>
              <p className="text-sm text-gray-600">Review and approve token redemption requests</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Loading redemption requests...</h3>
            <p className="mt-1 text-sm text-gray-500">Please wait while we fetch the latest data.</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12">
            <XCircle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading redemption requests</h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <button
              onClick={() => {
                setError(null);
                setRefreshTrigger(prev => prev + 1);
              }}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
              <div className="text-sm text-green-800 whitespace-pre-line">
                {successMessage}
              </div>
              <button
                onClick={() => setSuccessMessage('')}
                className="ml-auto flex-shrink-0 text-green-500 hover:text-green-700"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Content - only show when not loading and no error */}
        {!loading && !error && (
          <>
            {/* Debug Information */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-medium text-blue-900 mb-2">Debug Information</h3>
                <div className="text-xs text-blue-800 space-y-1">
                  <div>Total Redemption Requests: {redemptionRequests.length}</div>
                  <div>Filtered Requests: {filteredRequests.length}</div>
                  <div>Current Status Filter: {statusFilter}</div>
                  <div>Last Refresh: {new Date().toLocaleTimeString()}</div>
                </div>
              </div>
            )}

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Clock className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Pending Review
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {redemptionRequests.filter(r => r.status === 'Pending Review').length}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Approved
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {redemptionRequests.filter(r => r.status === 'Approved').length}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <XCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Rejected
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {redemptionRequests.filter(r => r.status === 'Rejected').length}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <DollarSign className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Value
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {formatCurrency(redemptionRequests.reduce((sum, r) => sum + r.redemptionValue, 0))}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Redemption Requests</h2>
                <p className="text-sm text-gray-600">Review and process token redemption requests</p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setRefreshTrigger(prev => prev + 1)}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status Filter</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending_review">Pending Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Redemption Requests Table */}
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bond Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Investor & Bond
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Redemption Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Urgency
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Request Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaction Hash
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRequests.map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {request.bondName || 'Unknown Bond'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.isMatured ? 'Maturity Redemption' : 'Early Redemption'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {request.investorName}
                            </div>
                            <div className="text-xs text-gray-400">
                              Investor: {request.investorId}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(request.redemptionValue)}
                            </div>
                            <div className="text-sm text-gray-500">
                              Principal: {formatCurrency(request.principalAmount)}
                            </div>
                            <div className="text-sm text-gray-500">
                              Interest: {formatCurrency(request.accruedInterest)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getUrgencyBadge(request.urgency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(request.requestDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(request.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {request.transactionHash ? (
                              <div>
                                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                  {request.transactionHash.substring(0, 16)}...
                                </span>
                                <button
                                  onClick={() => navigator.clipboard.writeText(request.transactionHash)}
                                  className="ml-2 text-blue-600 hover:text-blue-800 text-xs"
                                  title="Copy full hash"
                                >
                                  Copy
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedRequest(request);
                                alert(`Request Details:\n${JSON.stringify(request, null, 2)}`);
                              }}
                              className="text-blue-600 hover:text-blue-900 flex items-center"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </button>
                            {request.status === 'Pending Review' && (
                              <>
                                <button
                                  onClick={() => handleApprove(request)}
                                  className="text-green-600 hover:text-green-900 flex items-center"
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleReject(request)}
                                  className="text-red-600 hover:text-red-900 flex items-center"
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {filteredRequests.length === 0 && !loading && !error && (
              <div className="text-center py-12">
                <Banknote className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No redemption requests found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No redemption requests match the current filter criteria.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Approve Redemption Request
              </h3>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                You are about to approve the following redemption request:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><strong>Bond ID:</strong></div>
                  <div>{selectedRequest.bondId}</div>
                  <div><strong>Investor:</strong></div>
                  <div>{selectedRequest.investorName}</div>
                  <div><strong>Bond:</strong></div>
                  <div>{selectedRequest.bondName}</div>
                  <div><strong>Bond Amount:</strong></div>
                  <div>{formatCurrency(selectedRequest.tokenAmount)}</div>
                  <div><strong>Redemption Value:</strong></div>
                  <div className="font-medium text-green-600">{formatCurrency(selectedRequest.redemptionValue)}</div>
                  <div><strong>Payment Method:</strong></div>
                  <div>{selectedRequest.paymentMethod}</div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                  <h4 className="font-medium text-blue-900 mb-2">Bank Details</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <div>Account: {selectedRequest.bankDetails.accountName}</div>
                    <div>Number: {selectedRequest.bankDetails.accountNumber}</div>
                    <div>Bank: {selectedRequest.bankDetails.bankName}</div>
                    <div>SWIFT: {selectedRequest.bankDetails.swiftCode}</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-700">
                  <strong>Note:</strong> Approval will set token status to "approved_for_redeem" and generate an audit record with action time and user ID.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmApproval}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
              >
                Approve Redemption
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center mb-4">
              <XCircle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Reject Redemption Request
              </h3>
            </div>
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="text-sm">
                  <div><strong>Bond ID:</strong> {selectedRequest.bondId}</div>
                  <div><strong>Investor:</strong> {selectedRequest.investorName}</div>
                  <div><strong>Bond:</strong> {selectedRequest.bondName}</div>
                  <div><strong>Amount:</strong> {formatCurrency(selectedRequest.redemptionValue)}</div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a detailed reason for rejecting this redemption request..."
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRejectionModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRejection}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                Reject Request
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default RedemptionReview; 